// Import layout configuration
import {
    getConfig,
    loadDefaultConfig,
    ensureDefaultConfigForCurrentOrientation,
} from './ui/LayoutConfig.js';
import LayoutConfigWizard from './ui/LayoutConfigWizard.js';

// Set up Canvas
export const canvas = document.querySelector('canvas');
export const c = canvas.getContext('2d');
c.imageSmoothingEnabled = false;
c.textBaseline = 'top';
c.textRendering = 'geometricPrecision';

// Set original canvas resolution
canvas.width = 240;
canvas.height = 160;

function resizeCanvasAndControls() {
    const isPortrait = window.innerHeight > window.innerWidth;
    const config = getConfig(isPortrait);
    const topGap = config.screenTopOffset || 32; // Gap from top of viewport

    const controlButtons = document.querySelectorAll('.control-button');
    const dpadContainer = document.querySelector('.controls-dpad');
    const startSelectContainer = document.querySelector('.controls-start-select');
    const abContainer = document.querySelector('.controls-ab');

    // Canvas border width (from inline styles)
    const canvasBorderWidth = 16;
    const totalBorderWidth = canvasBorderWidth * 2; // Border on both sides

    // Calculate canvas scale
    let scale;
    let scaledWidth;
    let scaledHeight;

    if (config.gameScreenScale === 'fill') {
        // Fill screen mode: use minimum of width and height
        // Only works when discrete multipliers is disabled
        if (config.useDiscreteMultipliers) {
            // Fall back to auto if discrete multipliers is enabled
            // Treat as auto mode
            config.gameScreenScale = null;
        } else {
            // Calculate actual pixel dimensions to fill the screen (ignore free scale size)
            const minDimension = Math.min(window.innerWidth, window.innerHeight);
            // Account for border if enabled
            const borderSize = config.screenBorderEnabled ? totalBorderWidth : 0;
            const availableSize = minDimension - borderSize;

            // Calculate scale based on the smaller canvas dimension (height is smaller: 160 vs 240)
            const scaleByHeight = availableSize / canvas.height;
            // Calculate scale based on width
            const scaleByWidth = (window.innerWidth - borderSize) / canvas.width;

            // Use the smaller scale to ensure it fits both dimensions
            scale = Math.min(scaleByHeight, scaleByWidth);

            // Calculate actual pixel dimensions (free scaling)
            scaledWidth = canvas.width * scale;
            scaledHeight = canvas.height * scale;

            // Ensure minimum size
            if (scaledWidth < canvas.width) scaledWidth = canvas.width;
            if (scaledHeight < canvas.height) scaledHeight = canvas.height;
        }
    }

    // Handle custom scale or auto (including fallback from fill screen with discrete multipliers)
    if (config.gameScreenScale !== 'fill') {
        if (config.gameScreenScale !== null) {
            // Use custom scale multiplier
            if (config.useDiscreteMultipliers) {
                // Integer scaling for pixel-perfect
                scale = config.gameScreenScale;
                scaledWidth = canvas.width * scale;
                scaledHeight = canvas.height * scale;
            } else {
                // Use free scale size (when discrete multipliers is off)
                scale = config.freeScaleSize;
                scaledWidth = canvas.width * scale;
                scaledHeight = canvas.height * scale;
            }
        } else {
            // Auto-calculate scale based on viewport
            const borderSize = config.screenBorderEnabled ? totalBorderWidth : 0;

            if (config.useDiscreteMultipliers) {
                // Use integer scaling
                if (isPortrait) {
                    // Portrait: prioritize width
                    const availableWidth = window.innerWidth;
                    const availableHeight = window.innerHeight - topGap;
                    // Account for border: available space = viewport - border
                    const scaleWidth = Math.floor((availableWidth - borderSize) / canvas.width);
                    const scaleHeight = Math.floor((availableHeight - borderSize) / canvas.height);
                    scale = Math.min(scaleWidth, scaleHeight);
                } else {
                    // Landscape: prioritize height
                    const availableHeight = window.innerHeight - topGap;
                    scale = Math.floor((availableHeight - borderSize) / canvas.height);
                    // Check width constraint
                    const availableWidth = window.innerWidth;
                    const maxScaleByWidth = Math.floor((availableWidth - borderSize) / canvas.width);
                    scale = Math.min(scale, maxScaleByWidth);
                }

                // Ensure scale is at least 1
                scale = Math.max(1, scale);

                // Calculate scaled canvas dimensions (pixel-perfect multiples)
                scaledWidth = canvas.width * scale;
                scaledHeight = canvas.height * scale;
            } else {
                // Use free scaling
                scale = config.freeScaleSize;
                scaledWidth = canvas.width * scale;
                scaledHeight = canvas.height * scale;
            }
        }
    }

    // Position canvas: top-centered with configurable gap from top
    canvas.style.width = `${scaledWidth}px`;
    canvas.style.height = `${scaledHeight}px`;
    canvas.style.marginTop = `${topGap}px`;
    canvas.style.marginLeft = 'auto';
    canvas.style.marginRight = 'auto';
    canvas.style.display = 'block';
    canvas.style.imageRendering = config.imageRendering || 'pixelated';

    // Apply border settings
    if (config.screenBorderEnabled) {
        canvas.style.border = `${canvasBorderWidth}px solid rgb(25, 25, 25)`;
        canvas.style.borderRadius = `${config.screenBorderRadius}px`;
    } else {
        canvas.style.border = 'none';
        canvas.style.borderRadius = `${config.screenBorderRadius}px`;
    }

    // Add drop shadow to game screen
    canvas.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';

    // Get button sizes from config
    const dpadSize = config.buttonSizes.dpad;
    const abSize = config.buttonSizes.ab;
    const startSelectSize = config.buttonSizes.startSelect;

    // Get button font sizes from config
    const abFontSize = config.buttonFontSizes?.ab || 20;
    const startSelectFontSize = config.buttonFontSizes?.startSelect || 20;

    // D-Pad positioning (bottom-left)
    if (dpadContainer) {
        dpadContainer.style.left = `${config.offsets.dpad.left}px`;
        dpadContainer.style.bottom = `${config.offsets.dpad.bottom}px`;
    }

    // Start/Select positioning (bottom-center)
    if (startSelectContainer) {
        startSelectContainer.style.left = '50%';
        startSelectContainer.style.transform = 'translateX(-50%)';
        startSelectContainer.style.bottom = `${config.offsets.startSelect.bottom}px`;
        // Set gap between Start and Select buttons
        const startSelectButtonsContainer = startSelectContainer.querySelector('.action-buttons');
        if (startSelectButtonsContainer) {
            const gap = config.offsets.startSelect.gap || 16;
            startSelectButtonsContainer.style.gap = `${gap}px`;
        }
    }

    // A/B buttons positioning (bottom-right)
    if (abContainer) {
        abContainer.style.right = `${config.offsets.ab.right}px`;
        abContainer.style.bottom = `${config.offsets.ab.bottom}px`;
        // Set gap between A and B buttons
        const abButtonsContainer = abContainer.querySelector('.action-buttons');
        if (abButtonsContainer) {
            const gap = config.offsets.ab.gap || 16;
            abButtonsContainer.style.gap = `${gap}px`;

            // Apply vertical alignment for diagonal ergonomic positioning
            const verticalAlignment =
                config.offsets.ab.verticalAlignment !== undefined ? config.offsets.ab.verticalAlignment : 50;
            const alignmentOffset = (verticalAlignment - 50) / 50; // Convert 0-100% to -1 to +1 scale

            // Maximum vertical offset (in pixels) - adjust based on button size
            const maxOffset = abSize * 0.8; // 80% of button size
            const verticalOffset = alignmentOffset * maxOffset;

            // B button (first child) - above 50% moves up, below 50% moves down
            // A button (second child) - above 50% moves down, below 50% moves up
            const bButton = abButtonsContainer.querySelector('button:first-child');
            const aButton = abButtonsContainer.querySelector('button:last-child');

            if (bButton) {
                // B button: positive offset moves up (negative translateY)
                bButton.style.transform = `translateY(${-verticalOffset}px)`;
            }
            if (aButton) {
                // A button: positive offset moves down (positive translateY)
                aButton.style.transform = `translateY(${verticalOffset}px)`;
            }
        }
    }

    // Size buttons based on their control group
    controlButtons.forEach((button) => {
        let buttonSize;
        let isStartSelect = false;

        // Determine which group this button belongs to
        const parentContainer = button.closest('.controls-dpad, .controls-start-select, .controls-ab');
        if (parentContainer) {
            if (parentContainer.classList.contains('controls-dpad')) {
                buttonSize = dpadSize;
            } else if (parentContainer.classList.contains('controls-start-select')) {
                // Start/Select buttons are rectangular
                buttonSize = startSelectSize;
                isStartSelect = true;
            } else if (parentContainer.classList.contains('controls-ab')) {
                buttonSize = abSize;
            }
        }

        // Fallback to dpad size if not determined
        if (!buttonSize) {
            buttonSize = dpadSize;
        }

        // Apply size with !important to override any CSS
        if (
            isStartSelect &&
            (button.classList.contains('start-button') || button.classList.contains('select-button'))
        ) {
            // Start/Select buttons are rectangular: width is larger than height
            // Maintain aspect ratio: width is approximately 2x height
            button.style.setProperty('width', `${buttonSize * 2}px`, 'important');
            button.style.setProperty('height', `${buttonSize}px`, 'important');
            button.style.setProperty('min-width', `${buttonSize * 2}px`, 'important');
            button.style.setProperty('max-width', `${buttonSize * 2}px`, 'important');
            button.style.setProperty('min-height', `${buttonSize}px`, 'important');
            button.style.setProperty('max-height', `${buttonSize}px`, 'important');
        } else {
            // Square buttons - enforce fixed size
            button.style.setProperty('width', `${buttonSize}px`, 'important');
            button.style.setProperty('height', `${buttonSize}px`, 'important');
            button.style.setProperty('min-width', `${buttonSize}px`, 'important');
            button.style.setProperty('max-width', `${buttonSize}px`, 'important');
            button.style.setProperty('min-height', `${buttonSize}px`, 'important');
            button.style.setProperty('max-height', `${buttonSize}px`, 'important');
        }

        // Apply border radii
        setScaledBorderRadii(button);

        // Apply font sizes based on button group
        if (parentContainer) {
            if (parentContainer.classList.contains('controls-ab')) {
                // A/B buttons
                button.style.setProperty('font-size', `${abFontSize}px`, 'important');
            } else if (
                parentContainer.classList.contains('controls-start-select') &&
                (button.classList.contains('start-button') || button.classList.contains('select-button'))
            ) {
                // Start/Select buttons
                button.style.setProperty('font-size', `${startSelectFontSize}px`, 'important');
            }
        }
    });

    // Ensure controls never render outside viewport
    // Clamp positions if needed
    if (dpadContainer) {
        const dpadRect = dpadContainer.getBoundingClientRect();
        if (dpadRect.left < 0) {
            dpadContainer.style.left = '0px';
        }
        if (dpadRect.bottom > window.innerHeight) {
            dpadContainer.style.bottom = '0px';
        }
    }

    if (abContainer) {
        const abRect = abContainer.getBoundingClientRect();
        if (abRect.right > window.innerWidth) {
            abContainer.style.right = '0px';
        }
        if (abRect.bottom > window.innerHeight) {
            abContainer.style.bottom = '0px';
        }
    }
}

/**
 * Helper function to set the border radii
 * according to each button’s class (w-button, s-button, etc.)
 */
function setScaledBorderRadii(button) {
    // Calculate corner radius based on the button width
    const buttonWidth = parseInt(button.style.width, 10);
    const cornerRadius = buttonWidth * 0.25; // same ratio as 16px : 64px

    // Reset them first, so each class is handled distinctly
    button.style.borderRadius = '0px';
    button.style.borderTopLeftRadius = '0px';
    button.style.borderTopRightRadius = '0px';
    button.style.borderBottomLeftRadius = '0px';
    button.style.borderBottomRightRadius = '0px';

    if (button.classList.contains('w-button')) {
        // W: top corners are round, bottom corners are 0
        button.style.borderTopLeftRadius = `${cornerRadius}px`;
        button.style.borderTopRightRadius = `${cornerRadius}px`;
    } else if (button.classList.contains('s-button')) {
        // S: bottom corners are round, top corners are 0
        button.style.borderBottomLeftRadius = `${cornerRadius}px`;
        button.style.borderBottomRightRadius = `${cornerRadius}px`;
    } else if (button.classList.contains('a-button')) {
        // A: left corners are round, right corners are 0
        button.style.borderTopLeftRadius = `${cornerRadius}px`;
        button.style.borderBottomLeftRadius = `${cornerRadius}px`;
    } else if (button.classList.contains('d-button')) {
        // D: right corners are round, left corners are 0
        button.style.borderTopRightRadius = `${cornerRadius}px`;
        button.style.borderBottomRightRadius = `${cornerRadius}px`;
    } else if (button.classList.contains('d-pad-middle')) {
        // If you have a "d-pad-middle" that is intentionally square
        button.style.borderRadius = '0px';
    } else {
        // Default: full round corners if no special class
        button.style.borderRadius = `${cornerRadius}px`;
    }
}

// Export resize function so it can be called when config changes
export function triggerResize() {
    resizeCanvasAndControls();
}

// Layout config: single-tap button opens wizard
let layoutWizard = null;
const layoutConfigTrigger = document.getElementById('layoutConfigTrigger');
if (layoutConfigTrigger) {
    layoutConfigTrigger.addEventListener('click', () => {
        const isPortrait = window.innerHeight > window.innerWidth;
        layoutWizard = new LayoutConfigWizard(isPortrait);
        layoutWizard.show();
    });
}

// Attach resize logic to window events
window.addEventListener('resize', resizeCanvasAndControls);
window.addEventListener('load', () => {
    resizeCanvasAndControls();
    // Load default UI config from API (applies only for first-time users)
    loadDefaultConfig().then(() => triggerResize());
});
// Also listen for orientation changes: load API default for new orientation if needed, then resize
window.addEventListener('orientationchange', () => {
    const delay = 100;
    setTimeout(() => {
        ensureDefaultConfigForCurrentOrientation().then(() => {
            resizeCanvasAndControls();
        });
    }, delay);
});

// Initial Canvas Background for Testing
c.fillStyle = 'white';
c.fillRect(0, 0, canvas.width, canvas.height);

// ========================
// == Keyboard Listeners ==
// ========================
window.addEventListener('keydown', (e) => {
    switch (e.key) {
        case 'a':
            keys.a.pressed = true;
            break;
        case 'A':
            keys.A.pressed = true;
            break;
        case 'b':
            keys.b.pressed = true;
            break;
        case 'B':
            keys.B.pressed = true;
            break;
        case 'c':
            keys.c.pressed = true;
            break;
        case 'C':
            keys.C.pressed = true;
            break;
        case 'd':
            keys.d.pressed = true;
            break;
        case 'D':
            keys.D.pressed = true;
            break;
        case 'e':
            keys.e.pressed = true;
            break;
        case 'E':
            keys.E.pressed = true;
            break;
        case 'f':
            keys.f.pressed = true;
            break;
        case 'F':
            keys.F.pressed = true;
            break;
        case 'g':
            keys.g.pressed = true;
            break;
        case 'G':
            keys.G.pressed = true;
            break;
        case 'h':
            keys.h.pressed = true;
            break;
        case 'H':
            keys.H.pressed = true;
            break;
        case 'i':
            keys.i.pressed = true;
            break;
        case 'I':
            keys.I.pressed = true;
            break;
        case 'j':
            keys.j.pressed = true;
            break;
        case 'J':
            keys.J.pressed = true;
            break;
        case 'k':
            keys.k.pressed = true;
            break;
        case 'K':
            keys.K.pressed = true;
            break;
        case 'l':
            keys.l.pressed = true;
            break;
        case 'L':
            keys.L.pressed = true;
            break;
        case 'm':
            keys.m.pressed = true;
            break;
        case 'M':
            keys.M.pressed = true;
            break;
        case 'n':
            keys.n.pressed = true;
            break;
        case 'N':
            keys.N.pressed = true;
            break;
        case 'o':
            keys.o.pressed = true;
            break;
        case 'O':
            keys.O.pressed = true;
            break;
        case 'p':
            keys.p.pressed = true;
            break;
        case 'P':
            keys.P.pressed = true;
            break;
        case 'q':
            keys.q.pressed = true;
            break;
        case 'Q':
            keys.Q.pressed = true;
            break;
        case 'r':
            keys.r.pressed = true;
            break;
        case 'R':
            keys.R.pressed = true;
            break;
        case 's':
            keys.s.pressed = true;
            break;
        case 'S':
            keys.S.pressed = true;
            break;
        case 't':
            keys.t.pressed = true;
            break;
        case 'T':
            keys.T.pressed = true;
            break;
        case 'u':
            keys.u.pressed = true;
            break;
        case 'U':
            keys.U.pressed = true;
            break;
        case 'v':
            keys.v.pressed = true;
            break;
        case 'V':
            keys.V.pressed = true;
            break;
        case 'w':
            keys.w.pressed = true;
            break;
        case 'W':
            keys.W.pressed = true;
            break;
        case 'x':
            keys.x.pressed = true;
            break;
        case 'X':
            keys.X.pressed = true;
            break;
        case 'y':
            keys.y.pressed = true;
            break;
        case 'Y':
            keys.Y.pressed = true;
            break;
        case 'z':
            keys.z.pressed = true;
            break;
        case 'Z':
            keys.Z.pressed = true;
            break;
        case ' ':
            keys.space.pressed = true;
            break;
        case 'Enter':
            keys.enter.pressed = true;
            break;
        case 'Shift':
            keys.shift.pressed = true;
            break;
    }
});

window.addEventListener('keyup', (e) => {
    switch (e.key) {
        case 'a':
            keys.a.pressed = false;
            break;
        case 'A':
            keys.A.pressed = false;
            break;
        case 'b':
            keys.b.pressed = false;
            break;
        case 'B':
            keys.B.pressed = false;
            break;
        case 'c':
            keys.c.pressed = false;
            break;
        case 'C':
            keys.C.pressed = false;
            break;
        case 'd':
            keys.d.pressed = false;
            break;
        case 'D':
            keys.D.pressed = false;
            break;
        case 'e':
            keys.e.pressed = false;
            break;
        case 'E':
            keys.E.pressed = false;
            break;
        case 'f':
            keys.f.pressed = false;
            break;
        case 'F':
            keys.F.pressed = false;
            break;
        case 'g':
            keys.g.pressed = false;
            break;
        case 'G':
            keys.G.pressed = false;
            break;
        case 'h':
            keys.h.pressed = false;
            break;
        case 'H':
            keys.H.pressed = false;
            break;
        case 'i':
            keys.i.pressed = false;
            break;
        case 'I':
            keys.I.pressed = false;
            break;
        case 'j':
            keys.j.pressed = false;
            break;
        case 'J':
            keys.J.pressed = false;
            break;
        case 'k':
            keys.k.pressed = false;
            break;
        case 'K':
            keys.K.pressed = false;
            break;
        case 'l':
            keys.l.pressed = false;
            break;
        case 'L':
            keys.L.pressed = false;
            break;
        case 'm':
            keys.m.pressed = false;
            break;
        case 'M':
            keys.M.pressed = false;
            break;
        case 'n':
            keys.n.pressed = false;
            break;
        case 'N':
            keys.N.pressed = false;
            break;
        case 'o':
            keys.o.pressed = false;
            break;
        case 'O':
            keys.O.pressed = false;
            break;
        case 'p':
            keys.p.pressed = false;
            break;
        case 'P':
            keys.P.pressed = false;
            break;
        case 'q':
            keys.q.pressed = false;
            break;
        case 'Q':
            keys.Q.pressed = false;
            break;
        case 'r':
            keys.r.pressed = false;
            break;
        case 'R':
            keys.R.pressed = false;
            break;
        case 's':
            keys.s.pressed = false;
            break;
        case 'S':
            keys.S.pressed = false;
            break;
        case 't':
            keys.t.pressed = false;
            break;
        case 'T':
            keys.T.pressed = false;
            break;
        case 'u':
            keys.u.pressed = false;
            break;
        case 'U':
            keys.U.pressed = false;
            break;
        case 'v':
            keys.v.pressed = false;
            break;
        case 'V':
            keys.V.pressed = false;
            break;
        case 'w':
            keys.w.pressed = false;
            break;
        case 'W':
            keys.W.pressed = false;
            break;
        case 'x':
            keys.x.pressed = false;
            break;
        case 'X':
            keys.X.pressed = false;
            break;
        case 'y':
            keys.y.pressed = false;
            break;
        case 'Y':
            keys.Y.pressed = false;
            break;
        case 'z':
            keys.z.pressed = false;
            break;
        case 'Z':
            keys.Z.pressed = false;
            break;
        case ' ':
            keys.space.pressed = false;
            break;
        case 'Enter':
            keys.enter.pressed = false;
            break;
        case 'Shift':
            keys.shift.pressed = false;
            break;
    }
});

// =========================
// == Mouse-Click Support ==
// =========================
// If you want old-school mouse drag (single pointer), keep this logic.
// If you don’t need mouse, you can remove it.
const controlButtons = document.querySelectorAll('.control-button');

function pressButton(button) {
    const key = button.getAttribute('data-key');
    if (keys[key]) {
        keys[key].pressed = true;
    }
    // Optional: Add a highlight for visual feedback
    button.classList.add('active-touch');
}

function releaseButton(button) {
    const key = button.getAttribute('data-key');
    if (keys[key]) {
        keys[key].pressed = false;
    }
    button.classList.remove('active-touch');
}

// Track if the mouse is currently down
let mouseIsDown = false;

// Mouse events for single pointer
controlButtons.forEach((button) => {
    button.addEventListener('mousedown', () => {
        mouseIsDown = true;
        pressButton(button);
    });
    button.addEventListener('mouseenter', () => {
        // If the mouse is down and we enter a new button, press it
        if (mouseIsDown) {
            pressButton(button);
        }
    });
    button.addEventListener('mouseleave', () => {
        // If we leave a button while mouse is down, release it
        if (mouseIsDown) {
            releaseButton(button);
        }
    });
    button.addEventListener('mouseup', () => {
        mouseIsDown = false;
        releaseButton(button);
    });
});

window.addEventListener('mouseup', () => {
    mouseIsDown = false;
    // Release all buttons if mouse is up
    controlButtons.forEach((btn) => releaseButton(btn));
});

// ==========================
// == Multi-Touch Tracking ==
// ==========================
// This allows pressing multiple buttons simultaneously (e.g., hold B while moving).
const activeTouches = {}; // Maps touch.identifier -> the DOM button

export function updateKeyHighlights() {
    // Loop over each property (key) in keys
    for (const key in keys) {
        // Find the corresponding button by data-key
        const button = document.querySelector(`.control-button[data-key="${key}"]`);
        // If there's no button, skip
        if (!button) continue;

        // If this key is pressed, change background color, else revert
        if (keys[key].pressed) {
            button.style.backgroundColor = 'rgb(45, 45, 45)';
        } else {
            button.style.backgroundColor = 'rgb(25, 25, 25)';
        }
    }
}

window.addEventListener(
    'touchstart',
    (e) => {
        // Check if touching modal elements - if so, allow default behavior
        const target = e.target;
        const isModalElement = target.closest('.layout-config-overlay, .layout-config-modal, .layout-wizard-overlay, .layout-wizard-panel, .layout-config-trigger');
        if (isModalElement) {
            return; // Allow default touch behavior for modal
        }

        e.preventDefault(); // Prevent mouse emulation for game controls
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const touchTarget = document.elementFromPoint(touch.clientX, touch.clientY);

            if (touchTarget && touchTarget.classList.contains('control-button')) {
                activeTouches[touch.identifier] = touchTarget;
                pressButton(touchTarget);
            }
        }
    },
    { passive: false }
);

window.addEventListener(
    'touchmove',
    (e) => {
        // Only prevent default if interacting with game controls, not modal
        const target = e.target;
        const isModalElement = target.closest('.layout-config-overlay, .layout-config-modal, .layout-wizard-overlay, .layout-wizard-panel, .layout-config-trigger');
        if (isModalElement) {
            return; // Allow default touch behavior for modal
        }

        e.preventDefault();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const oldButton = activeTouches[touch.identifier];
            const newTarget = document.elementFromPoint(touch.clientX, touch.clientY);

            // If finger moved off the old button
            if (oldButton && newTarget !== oldButton) {
                releaseButton(oldButton);
                delete activeTouches[touch.identifier];
            }

            // If the new target is also a button, press it
            if (newTarget && newTarget.classList.contains('control-button')) {
                if (activeTouches[touch.identifier] !== newTarget) {
                    activeTouches[touch.identifier] = newTarget;
                    pressButton(newTarget);
                }
            }
        }
    },
    { passive: false }
);

window.addEventListener(
    'touchend',
    (e) => {
        // Only prevent default if interacting with game controls, not modal
        const target = e.target;
        const isModalElement = target.closest('.layout-config-overlay, .layout-config-modal, .layout-wizard-overlay, .layout-wizard-panel, .layout-config-trigger');
        if (isModalElement) {
            return; // Allow default touch behavior for modal
        }

        e.preventDefault();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const oldButton = activeTouches[touch.identifier];
            if (oldButton) {
                releaseButton(oldButton);
                delete activeTouches[touch.identifier];
            }
        }
    },
    { passive: false }
);

window.addEventListener(
    'touchcancel',
    (e) => {
        // Only prevent default if interacting with game controls, not modal
        const target = e.target;
        const isModalElement = target.closest('.layout-config-overlay, .layout-config-modal, .layout-wizard-overlay, .layout-wizard-panel, .layout-config-trigger');
        if (isModalElement) {
            return; // Allow default touch behavior for modal
        }

        e.preventDefault();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const oldButton = activeTouches[touch.identifier];
            if (oldButton) {
                releaseButton(oldButton);
                delete activeTouches[touch.identifier];
            }
        }
    },
    { passive: false }
);

// ============================
// == Keys & Global Tracking ==
// ============================
export const keys = {
    // Letters (lowercase then uppercase)
    a: { pressed: false },
    A: { pressed: false },
    b: { pressed: false },
    B: { pressed: false },
    c: { pressed: false },
    C: { pressed: false },
    d: { pressed: false },
    D: { pressed: false },
    e: { pressed: false },
    E: { pressed: false },
    f: { pressed: false },
    F: { pressed: false },
    g: { pressed: false },
    G: { pressed: false },
    h: { pressed: false },
    H: { pressed: false },
    i: { pressed: false },
    I: { pressed: false },
    j: { pressed: false },
    J: { pressed: false },
    k: { pressed: false },
    K: { pressed: false },
    l: { pressed: false },
    L: { pressed: false },
    m: { pressed: false },
    M: { pressed: false },
    n: { pressed: false },
    N: { pressed: false },
    o: { pressed: false },
    O: { pressed: false },
    p: { pressed: false },
    P: { pressed: false },
    q: { pressed: false },
    Q: { pressed: false },
    r: { pressed: false },
    R: { pressed: false },
    s: { pressed: false },
    S: { pressed: false },
    t: { pressed: false },
    T: { pressed: false },
    u: { pressed: false },
    U: { pressed: false },
    v: { pressed: false },
    V: { pressed: false },
    w: { pressed: false },
    W: { pressed: false },
    x: { pressed: false },
    X: { pressed: false },
    y: { pressed: false },
    Y: { pressed: false },
    z: { pressed: false },
    Z: { pressed: false },

    space: { pressed: false },
    enter: { pressed: false },
    Enter: { pressed: false },
    shift: { pressed: false },
};
